import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Helper to decode JWT and check expiration
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // JWT structure: header.payload.signature
    const payload = token.split('.')[1];
    if (!payload) return true;
    
    const decoded = JSON.parse(atob(payload));
    const exp = decoded.exp;
    
    if (!exp) return true;
    
    // Check if token expires in less than 5 minutes (buffer time)
    const now = Date.now() / 1000;
    const bufferTime = 5 * 60; // 5 minutes buffer
    
    return exp < (now + bufferTime);
  } catch (error) {
    console.error("Error decoding token:", error);
    return true;
  }
};

// Helper to get token expiration time
const getTokenExpiration = (token) => {
  if (!token) return null;
  
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? new Date(decoded.exp * 1000) : null;
  } catch (error) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Validate token with backend
  const validateToken = useCallback(async (tokenToValidate) => {
    if (!tokenToValidate || isTokenExpired(tokenToValidate)) {
      return false;
    }
    
    try {
      // Make a simple authenticated request to validate token
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${tokenToValidate}` },
        timeout: 5000
      });
      return response.status === 200;
    } catch (error) {
      if (error.response?.status === 401) {
        return false;
      }
      // Network error - assume token is still valid
      return !isTokenExpired(tokenToValidate);
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      const storedToken = localStorage.getItem("admin_token");
      
      if (storedToken) {
        // Check if token is expired locally first
        if (isTokenExpired(storedToken)) {
          console.log("Token expired, clearing...");
          localStorage.removeItem("admin_token");
          setToken(null);
          setIsAuthenticated(false);
        } else {
          // Validate with backend
          const isValid = await validateToken(storedToken);
          if (isValid) {
            setToken(storedToken);
            setIsAuthenticated(true);
          } else {
            console.log("Token invalid, clearing...");
            localStorage.removeItem("admin_token");
            setToken(null);
            setIsAuthenticated(false);
          }
        }
      }
      
      setIsLoading(false);
    };
    
    initAuth();
  }, [validateToken]);

  // Periodic token check (every 5 minutes)
  useEffect(() => {
    if (!token) return;
    
    const checkInterval = setInterval(() => {
      if (isTokenExpired(token)) {
        console.log("Token expired during session, logging out...");
        logout();
        // Redirect to login
        window.location.href = '/admin/login';
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(checkInterval);
  }, [token]);

  const login = (newToken) => {
    if (!newToken || isTokenExpired(newToken)) {
      console.error("Attempted to login with invalid/expired token");
      return false;
    }
    
    localStorage.setItem("admin_token", newToken);
    setToken(newToken);
    setIsAuthenticated(true);
    
    const expiration = getTokenExpiration(newToken);
    if (expiration) {
      console.log("Token will expire at:", expiration.toLocaleString());
    }
    
    return true;
  };

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  const getAuthHeader = useCallback(() => {
    if (!token || isTokenExpired(token)) {
      // Token expired, trigger logout
      if (token) {
        console.log("Token expired when getting auth header");
        logout();
      }
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }, [token, logout]);

  // Check if token is about to expire (within 1 hour)
  const isTokenExpiringSoon = useCallback(() => {
    if (!token) return false;
    
    try {
      const payload = token.split('.')[1];
      if (!payload) return false;
      
      const decoded = JSON.parse(atob(payload));
      const exp = decoded.exp;
      
      if (!exp) return false;
      
      const now = Date.now() / 1000;
      const oneHour = 60 * 60;
      
      return exp < (now + oneHour);
    } catch (error) {
      return false;
    }
  }, [token]);

  return (
    <AuthContext.Provider 
      value={{ 
        token, 
        isAuthenticated, 
        isLoading,
        login, 
        logout, 
        getAuthHeader,
        isTokenExpired: () => isTokenExpired(token),
        isTokenExpiringSoon,
        validateToken: () => validateToken(token)
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
